import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Button } from "@design-system/Button/Button";
import { LinkIcon } from "@icons";
import { AccrualMatchPreview } from "@components/accounting/accrualReview/AccrualMatchPreview";
import {
    aggregateMatches,
    MatchSummaryItem,
} from "@components/accounting/transactionMatch/aggregateMatches";
import {
    TransactionPreview,
    TransactionPreviewData,
} from "../TransactionPreview/TransactionPreview";
import "./TransferMatchLink.scss";

interface TransferMatchLinkTransaction {
    id: number;
}

interface TransferMatchLinkProps {
    onClick?: (transaction: TransferMatchLinkTransaction) => void;
    transaction: TransferMatchLinkTransaction;
    /**
     * The matched transaction to preview in the tooltip. When present, the
     * tooltip shows this preview instead of the generic hint so it's clear
     * what a transaction is matched to without opening the match drawer.
     */
    matchPreview?: TransactionPreviewData | null;
    documentMatches?: readonly MatchSummaryItem[];
}

interface MatchLinkTooltipContentProps {
    matchPreview?: TransactionPreviewData | null;
    documentMatches: readonly MatchSummaryItem[];
}

const MatchLinkTooltipContent = ({
    matchPreview,
    documentMatches,
}: MatchLinkTooltipContentProps) => {
    const aggregated = aggregateMatches(documentMatches);
    if (aggregated.resolvedCount > 0) {
        return (
            <AccrualMatchPreview
                counterpartyName={aggregated.firstName}
                extraCounterpartyCount={aggregated.extraNamesCount}
                amount={aggregated.totalAmount}
                countLabel={aggregated.countLabel}
            />
        );
    }
    if (matchPreview) {
        return <TransactionPreview transaction={matchPreview} />;
    }
    return <>Click to view or change the transfer match.</>;
};

export const TransferMatchLink = ({
    onClick,
    transaction,
    matchPreview,
    documentMatches = [],
}: TransferMatchLinkProps) => {
    const isDocumentMatch = documentMatches.length > 0;
    if (!onClick) {
        return (
            <button className="transfer-match-link transfer-match-link--readonly">
                <LinkIcon
                    width={12}
                    height={12}
                    className="icon-color-gray-700"
                />
            </button>
        );
    }
    return (
        <Tooltip
            className={
                matchPreview || isDocumentMatch
                    ? "transfer-match-link__preview-tooltip"
                    : undefined
            }
            content={
                <MatchLinkTooltipContent
                    matchPreview={matchPreview}
                    documentMatches={documentMatches}
                />
            }
        >
            <Button
                iconOnly
                size="sm"
                variant="secondary"
                aria-label={
                    isDocumentMatch ? "View match" : "View transfer match"
                }
                onClick={() => onClick?.(transaction)}
            >
                <LinkIcon className="icon-color-gray-700" />
            </Button>
        </Tooltip>
    );
};
